import React, { useMemo, useState } from "react";
import { getValue } from "../../utils/object.utils";
import { DropdownOptions } from "../dropdownOptions";
import { SUBJECT_TYPE_OPTIONS } from "../../Constants/options/classType.options";
import { SubjectForm } from "./SubjectForm";
import { Modal, ModalSize } from "../Modal";
import { useUpdateSubject } from "../../hooks/subjectHooks/subject.hooks";
import { validateSubjectForm } from "./validateSubjectForm";
import { useDispatch } from "react-redux";
import {
  setSuccessMessage,
  setWarningMessage,
  setErrorMessage,
} from "../../Redux/Slices/alert.slice";
import { SOMETHING_WENT_WRONG } from "../../Constants/messages";

interface Props {
  open: boolean;
  onClose: () => unknown;
  subject: any;
}
export const EditSubjectModal = ({ open, onClose, subject }: Props) => {
  const dispatch = useDispatch();

  const subjectId = getValue(subject, "id");
  const subjectDetails = getValue(subject, "subject_details", []);
  const _subjectName = getValue(subject, "name");
  const _subjectType = getValue(subject, "subject_type");

  const [subjectName, setSubjectName] = useState("");

  const [subjectTypeOption, setSubjectTypeOption] = useState<
    DropdownOptions | null | undefined
  >(null);

  const [subjectClasses, setSubjectClasses] = useState<DropdownOptions[]>([]);
  const [classMarks, setClassMarks] = useState<Record<string, number | string>>(
    {}
  );

  const { isLoading: isSubjectUpdating, mutate: updateSubject } =
    useUpdateSubject({
      onSuccess: () => {
        dispatch(setSuccessMessage("Subject updated successfully."));
        onClose();
      },
      onError: (error: any) => {
        dispatch(
          setErrorMessage(
            getValue(error, "response.data.message") || SOMETHING_WENT_WRONG
          )
        );
      },
    });

  useMemo(() => {
    const selectedClasses = subjectDetails.map((detail: any) => ({
      text: getValue(detail, "class.name"),
      value: getValue(detail, "class_id"),
    }));

    const marksByClass = subjectDetails.reduce(
      (acc: Record<string, number | string>, detail: any) => {
        const classId = String(getValue(detail, "class_id", ""));
        const marks = getValue(detail, "subject_marks");
        acc[classId] =
          marks === null || marks === undefined ? "" : Number(marks);
        return acc;
      },
      {}
    );

    const subjectType = SUBJECT_TYPE_OPTIONS.find(
      ({ value }) => value === _subjectType
    );

    setSubjectTypeOption(subjectType);
    setSubjectClasses(selectedClasses);
    setClassMarks(marksByClass);
    setSubjectName(_subjectName);
  }, [subject]);

  const onClassChange = (options: DropdownOptions[]) => {
    setSubjectClasses(options);
    setClassMarks((prev) => {
      const next: Record<string, number | string> = {};
      options.forEach((option) => {
        const classId = String(getValue(option, "value", ""));
        next[classId] = prev[classId] ?? "";
      });
      return next;
    });
  };

  const onClassMarksChange = (
    classId: string | number,
    marks: number | string
  ) => {
    setClassMarks((prev) => ({
      ...prev,
      [String(classId)]: marks,
    }));
  };

  const handleUpdateSubject = () => {
    if (!subjectId) return;

    const classDetails = subjectClasses
      .map((classItem) => {
        const classId = getValue(classItem, "value");
        const marks = classMarks[String(classId)];
        return {
          class_id: Number(classId),
          subject_marks:
            marks === "" || marks === undefined || marks === null
              ? null
              : Number(marks),
        };
      })
      .filter(({ class_id }) => Boolean(class_id));

    const errorMessage = validateSubjectForm({
      name: subjectName,
      subject_type: subjectTypeOption?.value as string,
      classDetails,
    });

    if (errorMessage) {
      return dispatch(setWarningMessage(errorMessage));
    }

    updateSubject({
      subjectId,
      body: {
        name: subjectName,
        subject_type: subjectTypeOption?.value as string,
        classDetails,
      },
    });
  };

  return (
    <Modal
      show={open}
      onClose={onClose}
      modalHeader={"Edit subject"}
      modalSize={ModalSize.sm}
      submissionButton={{
        text: "Save",
        onClick: () => {
          handleUpdateSubject();
        },
        isSubmitLoading: isSubjectUpdating,
      }}
    >
      <SubjectForm
        subjectName={subjectName}
        onSubjectNameChange={setSubjectName}
        subjectType={subjectTypeOption as DropdownOptions}
        onSubjectTypeChange={setSubjectTypeOption}
        selectedClass={subjectClasses}
        onClassChange={onClassChange}
        classMarks={classMarks}
        onClassMarksChange={onClassMarksChange}
      />
    </Modal>
  );
};
